import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import * as icons from 'react-icons/fa'
import ReactTooltip from 'react-tooltip'
import { twMerge } from 'tailwind-merge'
import { FiInfo, FiX } from 'react-icons/fi'
import { differenceInDays } from 'date-fns'

import { Container } from './styles'
import { api, authHeaders } from '../../../services/api'
import Spinner from '../../../components/Spinner'
import { socket } from '../../../util/socket'
import useQueryHook from '../../../hooks/useQuery'
import logo from '../../../assets/images/logo2.png'
import { GlobalContext } from '../../../contexts/GlobalContext'

const trialDays = 30

export default function Dashboard() {
    const { planos_disponiveis } = useQueryHook()

    const { user, reload } = useContext(GlobalContext)

    const [isUpgradeAlertVisible, setIsUpgradeAlertVisible] = useState(true)

    const { data: pessoaModulos, refetch: refetchPessoaModulos } = useQuery({
        queryKey: ['get_hired_modules_on_dashboard'],
        queryFn: async () => {
            const response = await api.get('pessoa', {
                ...authHeaders(),
                params: {
                    modulos_contratados: 1
                }
            })

            return response.map(item => {
                // const diasAposAssinatura = differenceInDays(new Date(), new Date(item.data_adesao))
                // const diasRestantes = trialDays - diasAposAssinatura
                // const ePlanoGratis = item.plano.valor === 0

                return {
                    ...item,
                    // dias_restantes_trial: item.gratuidade || ePlanoGratis ? null : diasRestantes
                }
            })
        },
        refetchOnWindowFocus: false
    })

    const { data: modulosOferecidos, isFetching: isFetchingModulosDisponiveis } = useQuery({
        queryKey: ['get_enabled_modules_on_dashboard'],
        queryFn: async () => {
            const response = await api.get('modulo')

            return response
        },
        placeholderData: [],
        enabled: !!pessoaModulos
    })

    const { data: planosProntuario } = useQuery({
        queryKey: ['get-lista-planos-prontuario'],
        queryFn: async () => {
            try {
                const response = await api.get('plano', {
                    params: {
                        modulo_id: 1
                    }
                })

                let result = Object.groupBy(response, ({ identificador }) => {
                    if (identificador.startsWith('salvavidas')) {
                        return 'salvavidas'
                    }

                    if (identificador.startsWith('individual')) {
                        return 'individual'
                    }

                    if (identificador.startsWith('familia')) {
                        return 'familia'
                    }

                    if (identificador.startsWith('empresa')) {
                        return 'empresa'
                    }

                    if (identificador.startsWith('medico')) {
                        return 'medico'
                    }

                    throw new Error('Tipo de plano não tratado.')
                })

                return result
            } catch (e) {
                console.log(e)
            }
        },
        enabled: !!pessoaModulos,
        refetchOnWindowFocus: false
    })

    useEffect(() => {
        function afterLiberacaoModulo(data) {
            refetchPessoaModulos()

            toast.success(`O módulo ${data.modulo.descricao} está liberado!`)

            reload()
        }

        socket.addListener('modulo_liberacao', afterLiberacaoModulo)

        return () => {
            socket.removeListener('modulo_liberacao', afterLiberacaoModulo)
        }
    }, [])

    useEffect(() => {
        if (user) {
            function onConsentimentoAceito() {
                refetchPessoaModulos()

                toast.info('Seu convite foi aceito. O módulo já está disponível.')
            }

            socket.addListener('consentimento_aceitacao', onConsentimentoAceito)

            return () => {
                socket.removeListener('consentimento_aceitacao', onConsentimentoAceito)
            }
        }
    }, [user])

    let modulosAtivados = pessoaModulos?.filter(item => item.status !== 'cancelled' && !item.token_consentimento) || []

    let modulosAConsentir = pessoaModulos?.filter(item => item.status !== 'cancelled' && !item.e_titular && item.token_consentimento) || []

    modulosAtivados = modulosAtivados.reduce((lista, item) => {
        if (!lista.some(i => i.modulo_id === item.modulo_id)) {
            const itensDoModulo = modulosAtivados.filter(a => a.modulo_id === item.modulo_id)
            const itemTitular = itensDoModulo.find(it => it.e_titular)

            if (itemTitular) {
                return [...lista, item]
            } else {
                return [...lista, itensDoModulo[0]]
            }
        }

        return lista
    }, [])

    const modulosDisponiveis = modulosOferecidos.filter(item => ![...modulosAtivados, ...modulosAConsentir].some(m => m.modulo_id === item.id))

    if (!planosProntuario || !pessoaModulos) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <Container className="bg-slate-200">
            <div className="flex flex-col gap-4 mt-8">
                <h2 className="text-slate-800">Módulos ativados</h2>

                {!!modulosAtivados.length || !!modulosAConsentir.length ? (
                    <ul className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {modulosAtivados.map(item => {
                            const acessoLiberado = item.status === 'authorized'
                            const statusPagamento = item.status
                            const eMembro = !item.e_titular
                            const planoId = item.plano.id
                            const planoIdentificador = item.plano.identificador
                            const moduloId = item.modulo_id || item.plano.modulo_id

                            let upgrade = null

                            if (!item.gratuidade) {
                                switch (planoIdentificador) {
                                    case 'familia':
                                        const [planoAtual, planoUpgrade] = planosProntuario[planoIdentificador]
                                        const totalPlanoMensal = planoAtual.valor * 12
                                        const diferencaAnual = totalPlanoMensal - planoUpgrade?.valor
                                        const desconto = Math.round((diferencaAnual * 100) / totalPlanoMensal)

                                        upgrade = () => (
                                            <div className="flex flex-col gap-1 text-slate-600 font-normal text-sm bg-emerald-200 p-3 rounded-md">
                                                <FiX size={18} onClick={() => { setIsUpgradeAlertVisible(false) }} className="self-end hover:opacity-60 cursor-pointer" />

                                                <span dangerouslySetInnerHTML={{
                                                    __html: `Que tal um desconto de <span class="font-bold">${desconto}%</span> na anuidade do Prontuário da Saúde do Cidadão? Com o nosso plano anual, você economiza e fica despreocupado.`
                                                }} />

                                                <Link to={`/plano/upgrade/${planoId}/${planoUpgrade.id}`} className="self-end mt-3">
                                                    Conheça já!
                                                </Link>
                                            </div>
                                        )
                                        break
                                    case 'salvavidas':
                                    case 'salvavidas_anual':
                                    case 'individual':
                                    case 'individual_anual':
                                        upgrade = () => (
                                            <div className="flex flex-col gap-1 text-slate-600 font-normal text-sm bg-emerald-200 px-2 py-3 rounded-md">
                                                <FiX size={18} onClick={() => { setIsUpgradeAlertVisible(false) }} className="self-end hover:opacity-60 cursor-pointer" />

                                                <span dangerouslySetInnerHTML={{
                                                    __html: 'Que tal incluir mais pessoas e gerenciar todos os prontuários em um só lugar? Assine nosso <span class="font-semibold">Plano Família</span> e tenha o controle dos prontuários dos seus familiares.'
                                                }} />

                                                <Link to={`/plano/upgrade/${planoId}/9`} className="self-end mt-3">
                                                    Conheça já!
                                                </Link>
                                            </div>
                                        )
                                        break
                                }
                            }

                            return (
                                <li key={item.assinatura_id} className="h-fit rounded-md flex flex-col gap-4 items-center justify-center text-center font-bold">
                                    <Link
                                        to={acessoLiberado ? item.link_dashboard : '#'}
                                        className={
                                            `group w-full h-fit py-3 flex flex-col gap-4 items-center justify-center bg-white rounded-md grid grid-rows-[5rem_1rem_auto]
                                                ${acessoLiberado ? '' : 'cursor-not-allowed'} 
                                                ${acessoLiberado ? 'text-slate-700 hover:text-sky-500' : 'text-slate-500 hover:text-slate-500 hover:opacity-70'}
                                            `}
                                    >
                                        {moduloId === 1 ? (
                                            <img src={logo} alt="" className="w-[26px] h-[56px] object-contain group-hover:rotate-[10deg] transition-transform justify-self-center" />
                                        ) : React.createElement(icons[item.icone], { size: 56, className: 'group-hover:rotate-[10deg] transition-transform justify-self-center' })}

                                        <div className="flex flex-col">
                                            <span>{item.descricao}</span>

                                            {/* {item.e_titular && item.dias_restantes_trial > 0 && (
                                                <span className="text-slate-400 italic font-normal mt-1" style={{ fontSize: 12 }}>
                                                    Restam {item.dias_restantes_trial} dias gratuitos. Aproveite!
                                                </span>
                                            )} */}

                                            {eMembro && <span className="text-slate-300" style={{ fontSize: 11 }}>(membro)</span>}
                                        </div>

                                        {statusPagamento === 'pending' ? (
                                            <div className="flex flex-col gap-2">
                                                <span className="italic text-slate-500 font-thin animate-pulse" style={{ fontSize: 13, animationDuration: '500ms' }}>Processando seu pagamento.</span>

                                                <div
                                                    className="italic text-slate-500 font-thin gap-2 mx-6 grid grid-cols-[1rem_auto] items-center bg-slate-100 p-2 rounded-md mx-4 mb-2 cursor-help"
                                                    style={{ fontSize: 12 }}
                                                    data-tip="Nosso sistema antifraude está atuando para garantir a segurança do seu pagamento."
                                                    data-for="payment-info-tooltip"
                                                >
                                                    <FiInfo size={18} />

                                                    <span className="text-left">Pode levar até 2 horas. Você será notificado quando estiver tudo certo.</span>

                                                    <ReactTooltip
                                                        id="payment-info-tooltip"
                                                        place="bottom"
                                                        effect="solid"
                                                        type="light"
                                                        className="max-w-[200px]"
                                                    />
                                                </div>
                                            </div>
                                        ) : statusPagamento === 'paused' ? (
                                            <span className="italic text-slate-500 font-thin">Assinatura pausada.</span>
                                        ) : statusPagamento === 'cancelled' ? (
                                            <span className="italic text-slate-500 font-thin">Assinatura cancelada.</span>
                                        ) : ''}
                                    </Link>

                                    {isUpgradeAlertVisible && upgrade?.()}
                                </li>
                            )
                        })}

                        {modulosAConsentir.map(item => {
                            const url = `/auth/membro_familia/consentimento/${item.token_consentimento}`

                            return (
                                <li key={item.id} className="rounded-md flex flex-col gap-4 items-center justify-center text-center font-bold">
                                    <Link
                                        to={url}
                                        className={
                                            `group w-full py-3 flex flex-col gap-2 items-center justify-center bg-white rounded-md grid grid-rows-[5rem_1rem_auto]
                                            text-sky-500 hover:text-sky-400 cursor-pointer`
                                        }
                                    >
                                        {React.createElement(icons[item.icone], { size: 56, className: 'group-hover:rotate-[10deg] transition-transform justify-self-center' })}

                                        <span>{item.descricao}</span>

                                        <span className="text-slate-600 italic bg-yellow-300/50 rounded-md p-1 my-3" style={{ fontSize: 11 }}>
                                            Existe um convite pendente.
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                ) : !modulosAtivados.length ? (
                    <div className="bg-yellow-300 text-slate-800 flex justify-center items-center h-16 rounded-md">Você ainda não contratou nenhum módulo.</div>
                ) : (
                    <Spinner />
                )}
            </div>

            <div className={twMerge('flex flex-col gap-4', planos_disponiveis ? 'flash-border-animation' : '')}>
                <h2 className="text-slate-800" id="modulos-disponiveis-title">Módulos disponíveis</h2>

                {isFetchingModulosDisponiveis ? (
                    <Spinner />
                ) : (
                    <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {modulosDisponiveis.map(item => {
                            const estaHabilitado = item.id === 1

                            return (
                                <div key={item.id} className={`border border-white rounded-md flex items-center justify-center text-center font-bold ${!estaHabilitado ? 'opacity-40 hover:opacity-40' : ''}`}>
                                    <Link
                                        to={estaHabilitado ? `/auth/feature/${item.id}` : '#'}
                                        className={`group w-full h-56 flex flex-col gap-4 items-center justify-center text-slate-500 bg-white rounded-md ${!estaHabilitado ? 'cursor-not-allowed' : 'hover:text-slate-700'}`}
                                    >
                                        {item.id === 1 ? (
                                            <img src={logo} alt="" className="w-[26px] h-[56px] object-contain group-hover:rotate-[10deg] transition-transform justify-self-center" />
                                        ) : React.createElement(icons[item.icone], { size: 56, className: 'group-hover:rotate-[10deg] transition-transform justify-self-center' })}

                                        {item.descricao}

                                        {estaHabilitado ? (
                                            <span className="button">
                                                Saiba mais
                                            </span>
                                        ) : (
                                            <span className="italic font-normal" style={{ fontSize: 13 }}>
                                                Em breve
                                            </span>
                                        )}
                                    </Link>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </Container>
    )
}
